import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import leaveRequestService from '../services/leaveRequestService';
import staffService from '../services/staffService';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, ArrowCounterClockwise, PaperPlaneTilt, UsersThree, X, Trash } from '@phosphor-icons/react';

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

const ADMIN_ROLES = ['super_admin', 'school_admin', 'hr'];
const APPROVE_ROLES = ['super_admin', 'school_admin'];

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

const STATUS_COLORS = {
  pending: '#d97706',
  manager_approved: '#2563eb',
  approved: '#16a34a',
  rejected: '#dc2626',
  cancelled: '#6b7280',
  revoked: '#dc2626',
};

function monthRange(monthStr) {
  const [y, m] = monthStr.split('-').map(Number);
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  const toISO = (d) => d.toISOString().split('T')[0];
  return { start: toISO(start), end: toISO(end) };
}

function StatusBadge({ status }) {
  return (
    <span className="capitalize" style={{ color: STATUS_COLORS[status] || '#374151', fontWeight: 600 }}>
      {status}
    </span>
  );
}

function LeaveRequestsPage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const canApprove = APPROVE_ROLES.includes(user?.role);

  const [myRequests, setMyRequests] = useState([]);
  const [myBalances, setMyBalances] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ leave_type: 'annual', start_date: todayISO(), end_date: todayISO(), reason: '' });

  const [adminView, setAdminView] = useState('pending'); // pending | all
  const [adminRequests, setAdminRequests] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [pendingMyApproval, setPendingMyApproval] = useState([]);
  const [loadingPendingApproval, setLoadingPendingApproval] = useState(false);

  const [calendarMonth, setCalendarMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [calendarRecords, setCalendarRecords] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [coverageRequest, setCoverageRequest] = useState(null);
  const [coveragePeriods, setCoveragePeriods] = useState([]);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [assigningEntryId, setAssigningEntryId] = useState(null);
  const [bulkSubstitute, setBulkSubstitute] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  const loadMy = useCallback(() => {
    if (isAdmin) {
      setMyRequests([]);
      setMyBalances([]);
      setLoadingMy(false);
      return;
    }
    setLoadingMy(true);
    Promise.all([leaveRequestService.getMy(), leaveRequestService.getMyBalances(new Date().getFullYear())])
      .then(([requestsData, balancesData]) => {
        setMyRequests(requestsData.records || []);
        setMyBalances(balancesData.records || []);
      })
      .catch((err) => toast.error(err.message || 'Failed to load your leave data'))
      .finally(() => setLoadingMy(false));
  }, [isAdmin]);

  const loadAdmin = useCallback(() => {
    if (!isAdmin) return;
    setLoadingAdmin(true);
    leaveRequestService.list(adminView === 'pending' ? { status: 'pending' } : {})
      .then((data) => setAdminRequests(data.records || []))
      .catch((err) => toast.error(err.message || 'Failed to load leave requests'))
      .finally(() => setLoadingAdmin(false));
  }, [isAdmin, adminView]);

  const loadPendingMyApproval = useCallback(() => {
    if (isAdmin) {
      setPendingMyApproval([]);
      setLoadingPendingApproval(false);
      return;
    }
    setLoadingPendingApproval(true);
    leaveRequestService.getPendingMyApproval()
      .then((data) => setPendingMyApproval(data.records || []))
      .catch((err) => toast.error(err.message || 'Failed to load requests pending your approval'))
      .finally(() => setLoadingPendingApproval(false));
  }, [isAdmin]);

  const loadCalendar = useCallback(() => {
    if (!isAdmin) return;
    setLoadingCalendar(true);
    const { start, end } = monthRange(calendarMonth);
    leaveRequestService.getCalendar(start, end)
      .then((data) => setCalendarRecords(data.records || []))
      .catch((err) => toast.error(err.message || 'Failed to load leave calendar'))
      .finally(() => setLoadingCalendar(false));
  }, [isAdmin, calendarMonth]);

  useEffect(() => { loadMy(); }, [loadMy]);
  useEffect(() => { loadAdmin(); }, [loadAdmin]);
  useEffect(() => { loadPendingMyApproval(); }, [loadPendingMyApproval]);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  useEffect(() => {
    if (!canApprove) return;
    staffService.listTeachers()
      .then((data) => setTeachers(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => {});
  }, [canApprove]);

  const openCoverage = async (request) => {
    setCoverageRequest(request);
    setBulkSubstitute('');
    setLoadingCoverage(true);
    try {
      const result = await leaveRequestService.getCoverageNeeds(request.id);
      setCoveragePeriods(result.periods || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load coverage needs');
      setCoveragePeriods([]);
    } finally {
      setLoadingCoverage(false);
    }
  };

  const closeCoverage = () => {
    setCoverageRequest(null);
    setCoveragePeriods([]);
  };

  const handleAssignSubstitute = async (timetableEntryId, substituteTeacherId) => {
    if (!substituteTeacherId) return;
    setAssigningEntryId(timetableEntryId);
    try {
      await leaveRequestService.assignSubstitute(coverageRequest.id, timetableEntryId, substituteTeacherId);
      toast.success('Substitute assigned');
      openCoverage(coverageRequest);
    } catch (error) {
      toast.error(error.message || 'Failed to assign substitute');
    } finally {
      setAssigningEntryId(null);
    }
  };

  const handleRemoveSubstitute = async (timetableEntryId) => {
    setAssigningEntryId(timetableEntryId);
    try {
      await leaveRequestService.removeSubstituteAssignment(coverageRequest.id, timetableEntryId);
      toast.success('Substitute removed');
      openCoverage(coverageRequest);
    } catch (error) {
      toast.error(error.message || 'Failed to remove substitute');
    } finally {
      setAssigningEntryId(null);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkSubstitute) {
      toast.error('Choose a teacher to assign to all periods');
      return;
    }
    setBulkAssigning(true);
    try {
      const result = await leaveRequestService.bulkAssignSubstitute(coverageRequest.id, bulkSubstitute);
      if (result.conflicts?.length) {
        toast.error(`Assigned ${result.assigned}, but ${result.conflicts.length} period(s) conflicted — assign those individually`);
      } else {
        toast.success(`Assigned to ${result.assigned} period(s)`);
      }
      openCoverage(coverageRequest);
    } catch (error) {
      toast.error(error.message || 'Failed to bulk-assign substitute');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const result = await leaveRequestService.create(form);
      toast.success(result.message || `Leave request submitted (${result.days_requested} working day(s))`);
      setForm({ leave_type: 'annual', start_date: todayISO(), end_date: todayISO(), reason: '' });
      loadMy();
    } catch (error) {
      toast.error(error.message || 'Unable to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    try {
      await leaveRequestService.cancel(requestId);
      toast.success('Leave request cancelled');
      loadMy();
    } catch (error) {
      toast.error(error.message || 'Unable to cancel request');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const result = await leaveRequestService.approve(requestId);
      toast.success(result.message || 'Leave request approved');
      loadAdmin();
    } catch (error) {
      toast.error(error.message || 'Unable to approve request');
    }
  };

  const handleReject = (requestId) => {
    setPromptDialog({
      title: 'Reject Leave Request',
      message: 'Reason for rejecting (optional):',
      defaultValue: '',
      onSubmit: async (value) => {
        const reason = value || '';
        try {
          await leaveRequestService.reject(requestId, reason);
          toast.success('Leave request rejected');
          loadAdmin();
        } catch (error) {
          toast.error(error.message || 'Unable to reject request');
        }
      },
    });
  };

  const handleManagerApprove = async (requestId) => {
    try {
      const result = await leaveRequestService.managerApprove(requestId);
      toast.success(result.message || 'Leave request approved');
      loadPendingMyApproval();
    } catch (error) {
      toast.error(error.message || 'Unable to approve request');
    }
  };

  const handleManagerReject = (requestId) => {
    setPromptDialog({
      title: 'Reject Leave Request',
      message: 'Reason for rejecting (optional):',
      defaultValue: '',
      onSubmit: async (value) => {
        const reason = value || '';
        try {
          await leaveRequestService.managerReject(requestId, reason);
          toast.success('Leave request rejected');
          loadPendingMyApproval();
        } catch (error) {
          toast.error(error.message || 'Unable to reject request');
        }
      },
    });
  };

  const handleRevoke = (requestId) => {
    setConfirmDialog({
      title: 'Revoke Approved Leave',
      message: 'Revoke this approved leave? This restores the leave balance and removes the excused attendance days it created.',
      type: 'warning',
      confirmLabel: 'Revoke',
      onConfirm: () => {
        setPromptDialog({
          title: 'Revoke Approved Leave',
          message: 'Reason for revoking (optional):',
          defaultValue: '',
          onSubmit: async (value) => {
            const reason = value || '';
            try {
              const result = await leaveRequestService.revoke(requestId, reason);
              toast.success(result.message || 'Leave request revoked');
              loadAdmin();
            } catch (error) {
              toast.error(error.message || 'Unable to revoke request');
            }
          },
        });
      },
    });
  };

  const handleSeedBalances = async () => {
    try {
      setSeeding(true);
      const result = await leaveRequestService.seedBalances(new Date().getFullYear());
      toast.success(`Seeded ${result.created} balance(s), ${result.skipped_existing} already existed`);
    } catch (error) {
      toast.error(error.message || 'Unable to seed leave balances');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Leave Requests</h1>
            <p>Apply for leave and track your balance. {isAdmin && 'Approve or reject requests from your staff below.'}</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr', gap: 24, alignItems: 'start' }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Request leave</h3>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Leave type
                <select
                  value={form.leave_type}
                  onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                  style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                >
                  <option value="annual">Annual</option>
                  <option value="sick">Sick</option>
                  <option value="casual">Casual</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                Start date
                <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }} />
              </label>
              <label style={{ fontSize: 13 }}>
                End date
                <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Reason (optional)
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }} />
              </label>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                <PaperPlaneTilt size={16} /> {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </form>

            <h4 style={{ marginTop: 24 }}>Your balance ({new Date().getFullYear()})</h4>
            {myBalances.length === 0 ? (
              <p style={{ fontSize: 13, color: '#6b7280' }}>No balance seeded yet — ask HR to set your entitlements.</p>
            ) : (
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead><tr><th>Type</th><th>Used</th><th>Remaining</th></tr></thead>
                <tbody>
                  {myBalances.map((b) => (
                    <tr key={b.id}>
                      <td className="capitalize">{b.leave_type}</td>
                      <td>{b.used_days}</td>
                      <td>{b.remaining_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <div className="card" style={{ padding: 20, marginBottom: 20 }}>
              <h3 style={{ marginTop: 0 }}>Your requests</h3>
              {loadingMy ? (
                <div className="loading">Loading...</div>
              ) : myRequests.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6b7280' }}>No leave requests yet.</p>
              ) : (
                <table className="data-table">
                  <thead><tr><th>Type</th><th>Dates</th><th>Days</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {myRequests.map((r) => (
                      <tr key={r.id}>
                        <td className="capitalize">{r.leave_type}</td>
                        <td>{r.start_date} → {r.end_date}</td>
                        <td>{r.days_requested}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td>
                          {r.status === 'pending' && (
                            <button className="btn btn-sm btn-ghost" onClick={() => handleCancel(r.id)}>Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {!loadingPendingApproval && pendingMyApproval.length > 0 && (
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <h3 style={{ marginTop: 0 }}>Pending my approval</h3>
                <table className="data-table">
                  <thead><tr><th>Staff</th><th>Type</th><th>Dates</th><th>Days</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {pendingMyApproval.map((r) => (
                      <tr key={r.id}>
                        <td>{r.staff_id}</td>
                        <td className="capitalize">{r.leave_type}</td>
                        <td>{r.start_date} → {r.end_date}</td>
                        <td>{r.days_requested}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-success" onClick={() => handleManagerApprove(r.id)} title="Approve">
                            <CheckCircle size={14} />
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleManagerReject(r.id)} title="Reject">
                            <XCircle size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {isAdmin && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ marginTop: 0 }}>Staff leave requests</h3>
                  <button className="btn btn-secondary btn-sm" onClick={handleSeedBalances} disabled={seeding}>
                    {seeding ? 'Seeding...' : `Seed ${new Date().getFullYear()} balances`}
                  </button>
                </div>
                <div className="view-tabs" style={{ marginBottom: 12 }}>
                  <button className={`tab ${adminView === 'pending' ? 'active' : ''}`} onClick={() => setAdminView('pending')}>Pending</button>
                  <button className={`tab ${adminView === 'all' ? 'active' : ''}`} onClick={() => setAdminView('all')}>All</button>
                </div>
                {loadingAdmin ? (
                  <div className="loading">Loading...</div>
                ) : adminRequests.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>No requests to show.</p>
                ) : (
                  <table className="data-table">
                    <thead><tr><th>Staff</th><th>Type</th><th>Dates</th><th>Days</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                      {adminRequests.map((r) => (
                        <tr key={r.id}>
                          <td>{r.staff_id}</td>
                          <td className="capitalize">{r.leave_type}</td>
                          <td>{r.start_date} → {r.end_date}</td>
                          <td>{r.days_requested}</td>
                          <td><StatusBadge status={r.status} /></td>
                          <td style={{ display: 'flex', gap: 6 }}>
                            {(r.status === 'pending' || r.status === 'manager_approved') && canApprove && (
                              <button className="btn btn-sm btn-success" onClick={() => handleApprove(r.id)} title="Approve">
                                <CheckCircle size={14} />
                              </button>
                            )}
                            {(r.status === 'pending' || r.status === 'manager_approved') && (
                              <button className="btn btn-sm btn-danger" onClick={() => handleReject(r.id)} title="Reject">
                                <XCircle size={14} />
                              </button>
                            )}
                            {r.status === 'approved' && canApprove && (
                              <button className="btn btn-sm btn-ghost" onClick={() => openCoverage(r)} title="Substitute coverage">
                                <UsersThree size={14} />
                              </button>
                            )}
                            {r.status === 'approved' && canApprove && (
                              <button className="btn btn-sm btn-ghost" onClick={() => handleRevoke(r.id)} title="Revoke">
                                <ArrowCounterClockwise size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {isAdmin && (
              <div className="card" style={{ padding: 20, marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ marginTop: 0 }}>Leave calendar</h3>
                  <input
                    type="month"
                    value={calendarMonth}
                    onChange={(e) => setCalendarMonth(e.target.value)}
                    style={{ padding: 6, borderRadius: 6, border: '1px solid #d1d5db' }}
                  />
                </div>
                {loadingCalendar ? (
                  <div className="loading">Loading...</div>
                ) : calendarRecords.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>No approved leave in this month.</p>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {Object.entries(
                      calendarRecords.reduce((groups, rec) => {
                        const key = rec.start_date;
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(rec);
                        return groups;
                      }, {})
                    )
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([date, recs]) => (
                        <div key={date} style={{ borderLeft: '3px solid #2563eb', paddingLeft: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{date}</div>
                          {recs.map((rec) => (
                            <div key={rec.id} style={{ fontSize: 13, color: '#374151' }}>
                              {rec.staff_name} — <span className="capitalize">{rec.leave_type}</span> ({rec.start_date} → {rec.end_date})
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {coverageRequest && (
        <div className="modal-overlay" onClick={closeCoverage}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>Substitute coverage</h2>
              <button className="modal-close" onClick={closeCoverage}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-form">
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                Periods this teacher normally covers ({coverageRequest.start_date} → {coverageRequest.end_date}) that need a substitute.
              </p>

              {loadingCoverage ? (
                <div className="loading">Loading...</div>
              ) : coveragePeriods.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6b7280' }}>This teacher has no timetabled periods during the leave period.</p>
              ) : (
                <>
                  <div className="form-row" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Assign one teacher to all periods</label>
                      <select value={bulkSubstitute} onChange={(e) => setBulkSubstitute(e.target.value)}>
                        <option value="">Select teacher...</option>
                        {teachers.map((t) => (
                          <option key={t.id} value={t.id}>{t.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <button className="btn btn-secondary" onClick={handleBulkAssign} disabled={bulkAssigning}>
                      {bulkAssigning ? 'Assigning...' : 'Assign to all'}
                    </button>
                  </div>

                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Period</th>
                        <th>Class</th>
                        <th>Subject</th>
                        <th>Substitute</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {coveragePeriods.map((p) => (
                        <tr key={p.timetable_entry_id}>
                          <td>{DAY_LABELS[p.day_of_week] || p.day_of_week}</td>
                          <td>{p.period_name} ({p.start_time}-{p.end_time})</td>
                          <td>{p.class_name}</td>
                          <td>{p.subject_name}</td>
                          <td>
                            {p.substitute_teacher_id ? (
                              <span>{p.substitute_teacher_name}</span>
                            ) : (
                              <select
                                value=""
                                disabled={assigningEntryId === p.timetable_entry_id}
                                onChange={(e) => handleAssignSubstitute(p.timetable_entry_id, e.target.value)}
                              >
                                <option value="">Assign...</option>
                                {teachers.map((t) => (
                                  <option key={t.id} value={t.id}>{t.full_name}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td>
                            {p.substitute_teacher_id && (
                              <button
                                className="btn-icon"
                                title="Remove"
                                disabled={assigningEntryId === p.timetable_entry_id}
                                onClick={() => handleRemoveSubstitute(p.timetable_entry_id)}
                              >
                                <Trash size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason (optional)', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default LeaveRequestsPage;
